
import { useCallback } from 'react';

interface UseSearchKeyboardProps {
  query: string;
  isOpen: boolean;
  totalHits: number;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  setIsOpen: (open: boolean) => void;
  onExplicitClose?: () => void;
  performDirectSearch?: () => void;
}

export function useSearchKeyboard({
  query,
  isOpen,
  totalHits,
  selectedIndex,
  setSelectedIndex,
  setIsOpen,
  onExplicitClose,
  performDirectSearch
}: UseSearchKeyboardProps) {
  const triggerDirectSearch = useCallback(() => {
    if (query.trim() && query.length >= 2 && performDirectSearch) {
      performDirectSearch();
    } else if (query.trim() && query.length >= 2) {
      // Fallback to just opening results if performDirectSearch not available
      setIsOpen(true);
    }
  }, [query, setIsOpen, performDirectSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      triggerDirectSearch();
      return;
    }

    if (!isOpen || totalHits === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(selectedIndex < totalHits - 1 ? selectedIndex + 1 : selectedIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : 0);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
      onExplicitClose?.();
    }
  }, [isOpen, totalHits, selectedIndex, setSelectedIndex, setIsOpen, onExplicitClose, triggerDirectSearch]);

  return {
    handleKeyDown,
    triggerDirectSearch
  };
}
